"use client";

import * as React from "react";
import styles from "./styles.module.css";
import { IoCloseSharp } from "react-icons/io5";

const DialogContext = React.createContext({
  open: false,
  setOpen: () => {},
});

function Dialog({
  children,
  open: controlledOpen,
  setOpen: setControlledOpen,
}) {
  const [internalOpen, setInternalOpen] = React.useState(false);

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = setControlledOpen || setInternalOpen;

  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  return (
    <DialogContext.Provider value={{ open, setOpen }}>
      {children}
    </DialogContext.Provider>
  );
}

const DialogTrigger = React.forwardRef(function DialogTrigger(
  { className, children, ...props },
  ref
) {
  const { setOpen } = React.useContext(DialogContext);

  return (
    <section
      ref={ref}
      className={`${styles.dialogTrigger} ${className || ""}`}
      onClick={() => setOpen(true)}
      {...props}
    >
      {children}
    </section>
  );
});

const DialogContent = React.forwardRef(function DialogContent(
  { className, children, closeDisabled = false, ...props },
  ref
) {
  const { open, setOpen } = React.useContext(DialogContext);

  React.useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !closeDisabled) setOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, setOpen, closeDisabled]);

  if (!open) return null;

  const handleOverlayClick = (e) => {
    // Close when clicking on the overlay — unless close is disabled (e.g. mid-upload)
    if (!closeDisabled) setOpen(false);
  };

  const handleContentClick = (e) => {
    // Prevent clicks on the content from closing the dialog
    e.stopPropagation();
  };

  return (
    <div
      id="dialog-content"
      className={styles.dialogOverlay}
      onClick={handleOverlayClick}
    >
      <div className={styles.dialogPosition}>
        <div
          ref={ref}
          className={`${styles.dialogContent} ${className || ""}`}
          onClick={handleContentClick}
          {...props}
        >
          {children}
          <button
            className={styles.dialogClose}
            onClick={() => {
              if (!closeDisabled) setOpen(false);
            }}
            aria-label="Close"
            disabled={closeDisabled}
          >
            <IoCloseSharp color="black" />
          </button>
        </div>
      </div>
    </div>
  );
});

const DialogBody = React.forwardRef(function DialogBody(
  { className, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      className={`${styles.dialogBody} ${className || ""}`}
      {...props}
    />
  );
});

const DialogHeader = React.forwardRef(function DialogHeader(
  { className, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      className={`${styles.dialogHeader} ${className || ""}`}
      {...props}
    />
  );
});

const DialogTitle = React.forwardRef(function DialogTitle(
  { className, ...props },
  ref
) {
  return (
    <h2
      ref={ref}
      className={`${styles.dialogTitle} ${className || ""}`}
      {...props}
    />
  );
});

const DialogDescription = React.forwardRef(function DialogDescription(
  { className, ...props },
  ref
) {
  return (
    <p
      ref={ref}
      className={`${styles.dialogDescription} ${className || ""}`}
      {...props}
    />
  );
});

const DialogFooter = React.forwardRef(function DialogFooter(
  { className, ...props },
  ref
) {
  return (
    <div
      ref={ref}
      className={`${styles.dialogFooter} ${className || ""}`}
      {...props}
    />
  );
});

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogBody,
};
